import { useEffect, useState } from "react";
import { acceptInvite, getInvite } from "../../api/invites";
import { Field, PanelMessage, Spinner, friendlyError } from "./shared";
import { Shell } from "./Admin";
import styles from "./Admin.module.css";

// Public page at /cpanel/invite/:token — the link a "Team" invite email
// sends. The invited person has no login yet, so this never calls anything
// that requires auth: it looks up the invite by token, then lets them set
// their own password, which creates their account and signs them in.
export default function InviteAccept({ token }) {
  const [invite, setInvite] = useState(undefined); // undefined = loading, null = invalid
  const [form, setForm] = useState({ name: "", password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    getInvite(token)
      .then((inv) => {
        setInvite(inv);
        setForm((f) => ({ ...f, name: inv.name || "" }));
      })
      .catch(() => setInvite(null));
  }, [token]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (form.password !== form.confirm) {
      setError("Passwords don't match.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await acceptInvite(token, { password: form.password, name: form.name });
      // Full reload into the dashboard now that a session token is stored.
      window.location.href = `${process.env.PUBLIC_URL}/`;
    } catch (err) {
      setError(friendlyError(err));
      setBusy(false);
    }
  };

  if (invite === undefined) {
    return (
      <Shell>
        <div className={styles.notice}>
          <Spinner />
        </div>
      </Shell>
    );
  }

  if (invite === null) {
    return (
      <Shell>
        <PanelMessage>This invite link is invalid or has expired. Ask an admin to send a new one.</PanelMessage>
      </Shell>
    );
  }

  return (
    <Shell>
      <form className={styles.card} onSubmit={submit}>
        <span className={styles.badge}>Team invite</span>
        <h1>Join Visible Online</h1>
        <p className={styles.sub}>
          You've been invited as {invite.role === "admin" ? "an admin" : "a member"} · {invite.email}
        </p>
        <fieldset className={styles.group} disabled={busy}>
          <Field label="Your name" value={form.name} onChange={set("name")} />
          <Field
            label="Password"
            type="password"
            value={form.password}
            onChange={set("password")}
            required
            autoComplete="new-password"
            hint="At least 6 characters."
          />
          <Field
            label="Confirm password"
            type="password"
            value={form.confirm}
            onChange={set("confirm")}
            required
            autoComplete="new-password"
          />
        </fieldset>
        {error && <p className={styles.error}>{error}</p>}
        <button className={styles.primaryBtn} type="submit" disabled={busy}>
          {busy ? "Setting up…" : "Set password & sign in"}
        </button>
      </form>
    </Shell>
  );
}
